import { isEcprNumber } from '../lib/isEcprNumber';
import { IAddHouseholdMemberRequest } from '@/shared/interfaces';
import {
    Modal,
    Button,
    FormErrorText,
    Input,
    HorizontalSwitch,
} from '@/components';
import { R } from '@/features/profile/resources/ProfileResources.resx';
import { format, R as GlobalResources } from '@/Resources.resx';
import { useForm } from 'react-hook-form';
import {
    isEmail,
    isFullName,
    isNationalIdentityNumber,
    isUnderCertainAge,
} from '@/shared/validators';
import { useMutation } from '@tanstack/react-query';
import { Utils } from '@/shared/Utils';
import { useEffect, useState } from 'react';
import {
    ADOLESCENT_HAS_ACTIVE_CONSULTATION,
    CANNOT_INVITE_ADULTS,
    PARENTAL_AUTHORITY_CONFIRMATION_FAILED,
} from '@/features/shared/features/addOrEditChild/model';
import { useAtomValue } from 'jotai';
import {
    adolescentMinimumAgeAtom,
    adultMinimumAgeAtom,
    isAdolescentEnabledAtom,
} from '@/caregiverSpace/shared/model';
import { FormControl } from '@/caregiverSpace/shared/ui';
import { ModulusWarning } from '@/features/profile/shared/ui/ModulusWarning';
import { ParentalAuthorityWarning } from '@/features/profile/shared/ui/ParentalAuthorityWarning';

type AddHouseholdMemberModalProps = {
    onClose: (reload: boolean) => void;
    onSubmitMember: (data: IAddHouseholdMemberRequest) => Promise<unknown>;
    initialValues?: Partial<IAddHouseholdMemberRequest>;
    showAdolescentTerms?: boolean;
};

export const AddHouseholdMemberModal = ({
    onClose,
    onSubmitMember,
    initialValues,
    showAdolescentTerms = false,
}: AddHouseholdMemberModalProps) => {
    const {
        register,
        handleSubmit,
        formState: { errors },
        getValues,
    } = useForm<IAddHouseholdMemberRequest>({
        mode: 'onSubmit',
        defaultValues: {
            Name: initialValues?.Name ?? '',
            Cpr: initialValues?.Cpr ?? '',
            Email: initialValues?.Email ?? '',
        },
    });

    const adolescentMinimumAge = useAtomValue(adolescentMinimumAgeAtom);
    const adultMinimumAge = useAtomValue(adultMinimumAgeAtom);
    const isAdolescentEnabled = useAtomValue(isAdolescentEnabledAtom);

    const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
    const [hasAcceptedAdolescentTerms, setHasAcceptedAdolescentTerms] =
        useState(false);

    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [showCannotInviteAdultsWarning, setCannotInviteAdultsWarning] =
        useState(false);
    const [
        showAdolescentAlreadyHasInviteWarning,
        setAdolescentAlreadyHasInviteWarning,
    ] = useState(false);
    const [showParentalAuthorityWarning, setShowParentalAuthorityWarning] =
        useState(false);
    const [showModulusWarning, setShowModulusWarning] = useState(false);
    const minimumAllowedAge = isAdolescentEnabled
        ? adolescentMinimumAge
        : adultMinimumAge;

    const addHouseholdMemberMutation = useMutation({
        mutationFn: onSubmitMember,
        onSuccess: () => onClose(true),
        onError: (error) => {
            if (error.message.includes(CANNOT_INVITE_ADULTS)) {
                setCannotInviteAdultsWarning(true);
                return;
            }
            if (error.message.includes(ADOLESCENT_HAS_ACTIVE_CONSULTATION)) {
                setAdolescentAlreadyHasInviteWarning(true);
                return;
            }
            if (
                error.message.includes(PARENTAL_AUTHORITY_CONFIRMATION_FAILED)
            ) {
                setShowModulusWarning(false);
                setShowParentalAuthorityWarning(true);
                return;
            }

            setErrorMessage(error.message);
        },
    });

    const onSubmitWithoutParentalCheck = () => {
        if (addHouseholdMemberMutation.isPending) return;

        addHouseholdMemberMutation.mutate({
            ...getValues(),
            ShouldCheckParentalAuthority: false,
        });
    };

    useEffect(() => {
        setErrorMessage(null);
    }, [showModulusWarning]);

    const onSubmit = async () => {
        if (addHouseholdMemberMutation.isPending) return;

        const data = getValues();
        const isEcprInput = isEcprNumber(data.Cpr);

        if (
            !isEcprInput &&
            !Utils.cprModulusCheck(data.Cpr) &&
            !showModulusWarning
        ) {
            setShowParentalAuthorityWarning(false);
            setShowModulusWarning(true);
            return;
        }

        const shouldCheckParentalAuthority =
            !isEcprInput &&
            isAdolescentEnabled &&
            isUnderCertainAge(data.Cpr, adultMinimumAge);

        addHouseholdMemberMutation.mutate({
            ...data,
            ShouldCheckParentalAuthority: shouldCheckParentalAuthority,
        });
    };

    const onCancel = () => onClose(false);

    return (
        <>
            {!showCannotInviteAdultsWarning &&
                !showAdolescentAlreadyHasInviteWarning && (
                    <Modal onClose={onCancel} title={R.InviteFamilyMember}>
                        {showModulusWarning && (
                            <ModulusWarning
                                onBack={() => setShowModulusWarning(false)}
                                onConfirm={onSubmit}
                                cprNumber={getValues().Cpr}
                                isPending={addHouseholdMemberMutation.isPending}
                                errorMessage={errorMessage}
                            />
                        )}

                        {showParentalAuthorityWarning && (
                            <ParentalAuthorityWarning
                                onBack={() =>
                                    setShowParentalAuthorityWarning(false)
                                }
                                onConfirm={onSubmitWithoutParentalCheck}
                                isPending={addHouseholdMemberMutation.isPending}
                            />
                        )}

                        {!showModulusWarning &&
                            !showParentalAuthorityWarning && (
                                <form onSubmit={handleSubmit(onSubmit)}>
                                    <main>
                                        <FormControl
                                            label={R.Name}
                                            error={errors.Name?.message}
                                            className="mb-6"
                                        >
                                            <Input
                                                name={`add-household-member-modal-${R.Name}`}
                                                {...register('Name', {
                                                    validate: {
                                                        isFullName: (
                                                            value: string,
                                                        ) =>
                                                            isFullName(value) ||
                                                            R.PartnerNameRequired,
                                                    },
                                                })}
                                            />
                                        </FormControl>

                                        <FormControl
                                            label={R.CprNumber}
                                            error={errors.Cpr?.message}
                                            className="mb-6"
                                        >
                                            <Input
                                                name={`add-household-member-modal-${R.CprNumber}`}
                                                {...register('Cpr', {
                                                    required:
                                                        R.PartnerCprRequired,
                                                    // Normalize both CPR and eCPR before validation.
                                                    setValueAs: (value) =>
                                                        value
                                                            .replace(
                                                                /[-\s]/g,
                                                                '',
                                                            )
                                                            .toUpperCase(),
                                                    validate: {
                                                        validCpr: (
                                                            value: string,
                                                        ) =>
                                                            isNationalIdentityNumber(
                                                                value,
                                                            ) ||
                                                            isEcprNumber(
                                                                value,
                                                            ) ||
                                                            R.InvalidCpr,
                                                        isOverCertainAge: (
                                                            value: string,
                                                        ) =>
                                                            !isEcprNumber(
                                                                value,
                                                            ) &&
                                                            isUnderCertainAge(
                                                                value,
                                                                minimumAllowedAge,
                                                            )
                                                                ? format(
                                                                      GlobalResources.CprTooYoung,
                                                                      minimumAllowedAge.toString(),
                                                                  )
                                                                : true,
                                                    },
                                                })}
                                                onInput={(event) => {
                                                    const input =
                                                        event.currentTarget;
                                                    input.value =
                                                        input.value.replace(
                                                            /[^a-zA-Z0-9\s-]/g,
                                                            '',
                                                        );
                                                }}
                                            />
                                        </FormControl>

                                        <FormControl
                                            label={R.Email}
                                            error={errors.Email?.message}
                                            className="mb-6"
                                        >
                                            <Input
                                                name={`add-household-member-modal-${R.Email}`}
                                                {...register('Email', {
                                                    required:
                                                        R.PartnerEmailRequired,
                                                    validate: {
                                                        isEmail: (
                                                            value: string,
                                                        ) =>
                                                            isEmail(value) ||
                                                            R.InvalidEmail,
                                                    },
                                                })}
                                            />
                                        </FormControl>

                                        <FormErrorText
                                            message={errorMessage ?? ''}
                                        />
                                    </main>

                                    {!showAdolescentTerms && (
                                        <HorizontalSwitch
                                            title={R.AddHouseholdMemberTerms}
                                            checked={hasAcceptedTerms}
                                            onChangeChecked={
                                                setHasAcceptedTerms
                                            }
                                            switchOnLeft={true}
                                            useSmallText={true}
                                        />
                                    )}

                                    {showAdolescentTerms && (
                                        <div className="mt-4">
                                            <HorizontalSwitch
                                                title={R.AdolescentTerms}
                                                checked={
                                                    hasAcceptedAdolescentTerms
                                                }
                                                onChangeChecked={
                                                    setHasAcceptedAdolescentTerms
                                                }
                                                switchOnLeft={true}
                                                useSmallText={true}
                                            />
                                        </div>
                                    )}

                                    <footer className="mt-4 text-right">
                                        <Button
                                            variant="primary"
                                            type="submit"
                                            disabled={
                                                (!hasAcceptedTerms &&
                                                    !showAdolescentTerms) ||
                                                (!hasAcceptedAdolescentTerms &&
                                                    showAdolescentTerms) ||
                                                addHouseholdMemberMutation.isPending
                                            }
                                            className={
                                                addHouseholdMemberMutation.isPending
                                                    ? 'button--loading'
                                                    : ''
                                            }
                                        >
                                            {R.InviteFamilyMember}
                                        </Button>
                                    </footer>
                                </form>
                            )}
                    </Modal>
                )}

            {showCannotInviteAdultsWarning && (
                <Modal
                    title={R.CannotInviteAdultsTitle}
                    onClose={() => setCannotInviteAdultsWarning(false)}
                >
                    <div className="space-y-6 text-(--color-text-primary)">
                        <p>
                            {format(
                                R.CannotInviteAdultsText,
                                adultMinimumAge.toString(),
                                adolescentMinimumAge.toString(),
                                (adultMinimumAge - 1).toString(),
                            )}
                        </p>

                        <div className="text-right">
                            <Button
                                variant="primary"
                                onClick={() => onClose(false)}
                            >
                                {R.Understood}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}
            {showAdolescentAlreadyHasInviteWarning && (
                <Modal
                    title={R.AdolescentAlreadyHasInviteTitle}
                    onClose={() => setAdolescentAlreadyHasInviteWarning(false)}
                >
                    <div className="space-y-6 text-(--color-text-primary)">
                        <p>
                            {format(
                                R.AdolescentAlreadyHasInviteText,
                                adolescentMinimumAge.toString(),
                            )}
                        </p>

                        <div className="text-right">
                            <Button
                                variant="primary"
                                onClick={() => onClose(false)}
                            >
                                {R.Understood}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
};
